import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Settings, 
  Upload, 
  Users, 
  Calendar, 
  FileText, 
  Activity, 
  AlertTriangle,
  CheckCircle,
  ArrowRight,
  Zap
} from 'lucide-react';

import SessionSelector from './SessionSelector';
import BulkUpload from './BulkUpload';
import BulkOperationsProgress from './BulkOperationsProgress';
import BulkOperationsHistory from './BulkOperationsHistory';
import { BulkOperationsManager } from '@/lib/materials-versioning';
import { useToast } from '@/hooks/use-toast';

/**
 * BulkOperations - Main bulk operations interface for materials management
 * 
 * Features:
 * - Comprehensive session selection across multiple programs
 * - Bulk file upload with version control
 * - Real-time progress tracking
 * - Impact preview before execution
 * - Operation history and rollback capabilities
 * - Mobile-responsive design
 */
const BulkOperations = ({ className = "" }) => {
  const [activeTab, setActiveTab] = useState('setup');
  const [selectedSessions, setSelectedSessions] = useState([]);
  const [impact, setImpact] = useState(null);
  const [currentOperation, setCurrentOperation] = useState(null);
  const [operationHistory, setOperationHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleSessionsChange = (sessions) => {
    setSelectedSessions(sessions);
  };

  const handleImpactChange = (impactData) => {
    setImpact(impactData);
  };

  const handleUploadComplete = async (uploadResult) => {
    setLoading(true);
    
    try {
      // Create operation record using real service
      const operationData = {
        changeNotes: uploadResult.changeNotes,
        versionIncrementType: uploadResult.versionIncrementType,
        totalFiles: uploadResult.files.length,
        totalSize: uploadResult.totalSize,
        totalSOsAffected: impact?.totalSOsAffected || 0,
        affectedProgramsCount: impact?.affectedProgramsCount || 0
      };

      // Start the bulk operation
      const bulkResult = await BulkOperationsManager.performBulkUpdate(
        selectedSessions,
        uploadResult.files,
        operationData,
        'admin@avenue.ca' // TODO: Get from user context
      );

      const operation = {
        id: bulkResult.operationId,
        operationType: 'Bulk Upload',
        status: 'in_progress',
        startedAt: new Date().toISOString(),
        selectedSessions,
        files: uploadResult.files,
        changeNotes: uploadResult.changeNotes,
        versionIncrementType: uploadResult.versionIncrementType,
        totalSize: uploadResult.totalSize,
        impact: {
          ...impact,
          sessionsToSOsMap: selectedSessions.reduce((map, sessionId) => {
            map[sessionId] = Math.floor(Math.random() * 5) + 1; // Mock SO count per session
            return map;
          }, {})
        },
        bulkResult
      };

      setCurrentOperation(operation);
      setActiveTab('progress');
      
      toast({
        title: "Bulk Operation Started",
        description: `Starting update for ${selectedSessions.length} sessions with ${uploadResult.files.length} files.`,
      });
      
    } catch (error) {
      console.error('Failed to start bulk operation:', error);
      
      toast({
        title: "Operation Failed",
        description: "Failed to start bulk operation. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUploadProgress = (progressData) => {
    // Update current operation with progress
    if (currentOperation) {
      setCurrentOperation(prev => ({
        ...prev,
        progressData
      }));
    }
  };

  const handleOperationComplete = (completionResult) => {
    const completedOperation = {
      ...currentOperation,
      status: 'completed',
      completedAt: new Date().toISOString(),
      statistics: completionResult.statistics
    };

    setOperationHistory(prev => [completedOperation, ...prev]);
    setCurrentOperation(null);
    
    // Show success notification
    toast({
      title: "Operation Completed Successfully",
      description: `Bulk operation completed. ${completionResult.statistics?.versionsCreated || 0} versions created, ${completionResult.statistics?.sosAffected || 0} SOs affected.`,
    });
    
    // Reset form
    setSelectedSessions([]);
    setImpact(null);
    
    // Show success message and switch to history
    setTimeout(() => {
      setActiveTab('history');
    }, 2000);
  };

  const handleOperationCancel = () => {
    if (currentOperation) {
      const cancelledOperation = {
        ...currentOperation,
        status: 'cancelled',
        completedAt: new Date().toISOString()
      };
      
      setOperationHistory(prev => [cancelledOperation, ...prev]);
    }
    
    setCurrentOperation(null);
    setActiveTab('setup');
  };

  const handleOperationRetry = () => {
    if (currentOperation) {
      setCurrentOperation(prev => ({
        ...prev,
        status: 'in_progress',
        startedAt: new Date().toISOString()
      }));
    }
  };

  const handleRollback = async (rollbackData) => {
    setLoading(true);
    
    try {
      // Find the original operation to get session and version information
      const originalOperation = operationHistory.find(op => op.id === rollbackData.operationId);
      
      if (!originalOperation) {
        toast({
          title: "Rollback Failed",
          description: "Original operation not found. Cannot perform rollback.",
          variant: "destructive",
        });
        return;
      }

      // Perform bulk rollback using the real service
      const rollbackResult = await BulkOperationsManager.performBulkRollback(
        originalOperation.selectedSessions || [],
        [originalOperation.previousVersionId || 'v1.0.0'], // Fallback to v1.0.0
        'admin@avenue.ca', // TODO: Get from user context
        `Rollback of operation ${rollbackData.operationId}`
      );

      // Create rollback operation
      const rollbackOperation = {
        id: rollbackResult.operationId,
        operationType: 'Rollback Operation',
        status: 'in_progress',
        startedAt: new Date().toISOString(),
        targetOperationId: rollbackData.operationId,
        analysis: rollbackData.analysis,
        rollbackResult
      };

      setCurrentOperation(rollbackOperation);
      setActiveTab('progress');
      
      toast({
        title: "Rollback Started",
        description: `Rolling back operation ${rollbackData.operationId} affecting ${originalOperation.selectedSessions?.length || 0} sessions.`,
      });
      
    } catch (error) {
      console.error('Failed to perform rollback:', error);
      
      toast({
        title: "Rollback Failed",
        description: "Failed to start rollback operation. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewOperationDetails = (operation) => {
    // Could open a detailed modal or navigate to operation details page
    console.log('View operation details:', operation);
  };

  const canProceedToUpload = selectedSessions.length > 0;
  const hasActiveOperation = currentOperation && currentOperation.status === 'in_progress';

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold tracking-tight text-gray-900">
            Bulk Operations
          </h2>
          <p className="text-gray-600 mt-1">
            Manage materials across multiple sessions and programs
          </p>
        </div>
        
        {/* Quick Stats */}
        {impact && (
          <div className="flex items-center space-x-4 text-sm">
            <div className="flex items-center space-x-1 text-blue-600">
              <Calendar className="h-4 w-4" />
              <span>{impact.selectedSessionsCount} sessions</span>
            </div>
            <div className="flex items-center space-x-1 text-green-600">
              <Users className="h-4 w-4" />
              <span>{impact.totalSOsAffected} SOs</span>
            </div>
            <div className="flex items-center space-x-1 text-purple-600">
              <Settings className="h-4 w-4" />
              <span>{impact.affectedProgramsCount} programs</span>
            </div>
          </div>
        )}
      </div>

      {/* Main Interface */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-4 h-12 lg:h-9">
          <TabsTrigger 
            value="setup" 
            className="flex flex-col sm:flex-row items-center space-y-0 sm:space-y-0 sm:space-x-2 px-2 sm:px-3 text-xs sm:text-sm"
            disabled={hasActiveOperation}
          >
            <Settings className="h-3 w-3 sm:h-4 sm:w-4" />
            <span className="sm:hidden">Setup</span>
            <span className="hidden sm:inline">Setup</span>
          </TabsTrigger>
          <TabsTrigger 
            value="upload"
            className="flex flex-col sm:flex-row items-center space-y-0 sm:space-y-0 sm:space-x-2 px-2 sm:px-3 text-xs sm:text-sm"
            disabled={!canProceedToUpload || hasActiveOperation}
          >
            <Upload className="h-3 w-3 sm:h-4 sm:w-4" />
            <span className="sm:hidden">Upload</span>
            <span className="hidden sm:inline">Upload</span>
          </TabsTrigger>
          <TabsTrigger 
            value="progress"
            className="flex flex-col sm:flex-row items-center space-y-0 sm:space-y-0 sm:space-x-2 px-2 sm:px-3 text-xs sm:text-sm"
            disabled={!hasActiveOperation}
          >
            <Activity className="h-3 w-3 sm:h-4 sm:w-4" />
            <span className="sm:hidden">Progress</span>
            <span className="hidden sm:inline">Progress</span>
          </TabsTrigger>
          <TabsTrigger 
            value="history"
            className="flex flex-col sm:flex-row items-center space-y-0 sm:space-y-0 sm:space-x-2 px-2 sm:px-3 text-xs sm:text-sm"
          >
            <FileText className="h-3 w-3 sm:h-4 sm:w-4" />
            <span className="sm:hidden">History</span>
            <span className="hidden sm:inline">History</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="setup" className="space-y-6">
          {/* Impact Preview */}
          {impact && (
            <Card className="border-blue-200 bg-blue-50">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2 text-blue-900">
                  <Zap className="h-5 w-5" />
                  <span>Operation Impact Preview</span>
                </CardTitle>
                <CardDescription className="text-blue-700">
                  Review the scope of your bulk operation before proceeding
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-3 bg-white rounded-lg border border-blue-200">
                    <div className="flex items-center justify-center mb-2">
                      <Calendar className="h-5 w-5 text-blue-600" />
                    </div>
                    <p className="text-2xl font-bold text-blue-900">{impact.selectedSessionsCount}</p>
                    <p className="text-sm text-blue-600">Sessions Selected</p>
                  </div>
                  
                  <div className="text-center p-3 bg-white rounded-lg border border-blue-200">
                    <div className="flex items-center justify-center mb-2">
                      <Users className="h-5 w-5 text-green-600" />
                    </div>
                    <p className="text-2xl font-bold text-green-900">{impact.totalSOsAffected}</p>
                    <p className="text-sm text-green-600">SOs Affected</p>
                  </div>
                  
                  <div className="text-center p-3 bg-white rounded-lg border border-blue-200">
                    <div className="flex items-center justify-center mb-2">
                      <FileText className="h-5 w-5 text-purple-600" />
                    </div>
                    <p className="text-2xl font-bold text-purple-900">{impact.totalMaterialsAffected}</p>
                    <p className="text-sm text-purple-600">Materials Affected</p>
                  </div>
                  
                  <div className="text-center p-3 bg-white rounded-lg border border-blue-200">
                    <div className="flex items-center justify-center mb-2">
                      <Settings className="h-5 w-5 text-orange-600" />
                    </div>
                    <p className="text-2xl font-bold text-orange-900">{impact.affectedProgramsCount}</p>
                    <p className="text-sm text-orange-600">Programs Affected</p>
                  </div>
                </div>

                <div className="flex items-center justify-between mt-4 pt-4 border-t border-blue-200">
                  <div className="flex items-center space-x-2 text-sm text-blue-700">
                    <CheckCircle className="h-4 w-4" />
                    <span>Ready to proceed with bulk operation</span>
                  </div>
                  <Button 
                    onClick={() => setActiveTab('upload')}
                    className="flex items-center space-x-2"
                  >
                    <span>Proceed to Upload</span>
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Session Selection */}
          <SessionSelector
            selectedSessions={selectedSessions}
            onSessionsChange={handleSessionsChange}
            onImpactChange={handleImpactChange}
          />

          {/* Selection Summary */}
          {selectedSessions.length === 0 && (
            <Card className="border-gray-200">
              <CardContent className="p-6 text-center">
                <Calendar className="h-8 w-8 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600 font-medium">No sessions selected</p>
                <p className="text-gray-500 text-sm mt-1">
                  Select one or more sessions to begin your bulk operation
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="upload" className="space-y-6">
          {/* Upload Instructions */}
          <Card className="border-green-200 bg-green-50">
            <CardContent className="p-4">
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-green-900">
                    Ready for bulk upload
                  </p>
                  <p className="text-sm text-green-700 mt-1">
                    Materials will be applied to {selectedSessions.length} selected sessions affecting {impact?.totalSOsAffected || 0} SOs
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Bulk Upload */}
          <BulkUpload
            selectedSessions={selectedSessions}
            onUploadComplete={handleUploadComplete}
            onUploadProgress={handleUploadProgress}
          />
        </TabsContent>

        <TabsContent value="progress" className="space-y-6">
          <BulkOperationsProgress
            operation={currentOperation}
            onCancel={handleOperationCancel}
            onRetry={handleOperationRetry}
            onComplete={handleOperationComplete}
          />
        </TabsContent>

        <TabsContent value="history" className="space-y-6">
          <BulkOperationsHistory
            onRollback={handleRollback}
            onViewDetails={handleViewOperationDetails}
          />
        </TabsContent>
      </Tabs>

      {/* Warning for Active Operations */}
      {hasActiveOperation && (
        <div className="fixed bottom-4 right-4 max-w-sm">
          <Card className="border-orange-200 bg-orange-50">
            <CardContent className="p-4">
              <div className="flex items-start space-x-3">
                <AlertTriangle className="h-5 w-5 text-orange-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-orange-900">
                    Operation in Progress
                  </p>
                  <p className="text-sm text-orange-700 mt-1">
                    Please wait for the current operation to complete before starting a new one.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default BulkOperations;